import { useState, useEffect } from "react";
import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, Globe, ChevronDown, ShieldCheck, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { useContent } from "@/context/ContentContext";
import ChatWidget from "@/components/shared/ChatWidget";

export function Logo({ dark = false }) {
  return (
    <Link to="/" className="flex items-center" data-testid="nav-logo">
      <img
        src={dark ? "/brand/eleven-logo-light-sm.png" : "/brand/eleven-logo-dark-sm.png"}
        alt="Eleven Family Office"
        className="h-[22px] sm:h-6 w-auto shrink-0"
        data-testid="nav-logo-img"
      />
    </Link>
  );
}

function LanguageSwitcher({ dark = false }) {
  const { lang, setLang } = useLang();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`gap-1.5 rounded-[8px] ${dark ? "text-white/70 hover:text-white hover:bg-white/10" : "text-[#3B4452] hover:text-[#111318]"}`}
          data-testid="language-switcher"
        >
          <Globe className="h-4 w-4" />
          <span className="uppercase text-xs font-semibold">{lang}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setLang("de")} data-testid="lang-de">
          Deutsch {lang === "de" && "\u2713"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">
          English {lang === "en" && "\u2713"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Navbar() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const bereiche = [
    { to: "/family-office", label: "Family Office", testid: "nav-family-office" },
    { to: "/real-estate", label: "Real Estate", testid: "nav-real-estate" },
    { to: "/maklerservice", label: "Maklerservice", testid: "nav-maklerservice" },
    { to: "/development", label: "Development", testid: "nav-development" },
  ];
  const weitere = [
    { to: "/leistungen", label: t("Leistungs\u00fcbersicht", "Services overview"), testid: "nav-leistungen-uebersicht" },
    { to: "/privatkunden", label: t("Privatkunden", "Private Clients"), testid: "nav-privatkunden" },
    { to: "/leistungen/festgeld", label: t("Festgeld auf Anfrage", "Fixed-Term on Request"), testid: "nav-festgeld" },
    { to: "/karriere", label: t("Karriere & Partner", "Careers & Partners"), testid: "nav-karriere" },
  ];

  const navLinkCls = ({ isActive }) =>
    `relative whitespace-nowrap px-2 xl:px-3 py-2 text-[13px] xl:text-sm font-medium transition-colors duration-200 ${
      isActive
        ? "text-[#111318] after:content-[''] after:absolute after:-bottom-[2px] after:left-2 after:h-[2px] after:w-6 after:bg-[#FF3C20]"
        : "text-[#3B4452] hover:text-[#111318]"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b border-border bg-white/95 backdrop-blur-md transition-shadow duration-300 ${
        scrolled ? "" : ""
      }`}
      data-testid="site-header"
    >
      <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-nowrap items-center justify-between gap-3 transition-all duration-300 ${scrolled ? "h-16" : "h-[76px]"}`}>
        <div className="shrink-0">
          <Logo />
        </div>
        <nav className="hidden lg:flex flex-nowrap items-center gap-0.5 whitespace-nowrap">
          {bereiche.map((b) => (
            <NavLink key={b.to} to={b.to} className={navLinkCls} data-testid={b.testid}>
              {b.label}
            </NavLink>
          ))}
          <NavLink to="/berater-finden" className={navLinkCls} data-testid="nav-berater-finden">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-[#E8241B]" />
              {t("Berater finden", "Find an advisor")}
            </span>
          </NavLink>
          <NavLink to="/ueber-uns" className={navLinkCls} data-testid="nav-ueber-uns">
            {t("\u00dcber uns", "About us")}
          </NavLink>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1 whitespace-nowrap px-2 xl:px-3 text-[13px] xl:text-sm font-medium text-[#3B4452] hover:text-[#111318] hover:bg-[#F1F2F4] rounded-[6px]" data-testid="nav-weitere">
                {t("Weitere", "More")} <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              {weitere.map((s) => (
                <DropdownMenuItem key={s.to} onClick={() => navigate(s.to)} data-testid={s.testid}>
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
        <div className="flex shrink-0 flex-nowrap items-center gap-1.5">
          <LanguageSwitcher />
          {user ? (
            <Button size="sm" variant="ghost" className="rounded-[6px] hidden sm:inline-flex whitespace-nowrap text-[#3B4452] hover:text-[#111318]" onClick={() => navigate("/portal")} data-testid="nav-portal-button">
              Portal
            </Button>
          ) : (
            <Button size="sm" variant="ghost" className="rounded-[6px] hidden sm:inline-flex whitespace-nowrap text-[#3B4452] hover:text-[#111318]" onClick={() => navigate("/login")} data-testid="nav-login-button">
              Login
            </Button>
          )}
          <Button
            size="sm"
            className="rounded-[6px] h-10 px-4 bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold hidden xl:inline-flex whitespace-nowrap transition-colors duration-200"
            onClick={() => navigate("/anfrage/erstgespraech")}
            data-testid="nav-cta-button"
          >
            {t("Erstgespr\u00e4ch vereinbaren", "Book initial consultation")}
          </Button>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden rounded-[8px] text-[#111318]" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[310px] bg-white">
              <div className="mt-8 flex flex-col gap-1">
                <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9AA1AB]">{t("Leistungsbereiche", "Services")}</p>
                {bereiche.map((b) => (
                  <Link key={b.to} to={b.to} onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium rounded-[8px] hover:bg-[#F1F2F4]">
                    {b.label}
                  </Link>
                ))}
                <Link to="/berater-finden" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium rounded-[8px] hover:bg-[#F1F2F4] inline-flex items-center gap-1.5" data-testid="mobile-nav-berater-finden">
                  <MapPin className="h-4 w-4 text-[#E8241B]" />
                  {t("Berater finden", "Find an advisor")}
                </Link>
                <Link to="/ueber-uns" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium rounded-[8px] hover:bg-[#F1F2F4]">
                  {t("\u00dcber uns", "About us")}
                </Link>
                <Separator className="my-2" />
                {weitere.map((s) => (
                  <Link key={s.to} to={s.to} onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium rounded-[8px] hover:bg-[#F1F2F4]">
                    {s.label}
                  </Link>
                ))}
                <Separator className="my-2" />
                <Link to={user ? "/portal" : "/login"} onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium rounded-[8px] hover:bg-[#F1F2F4]">
                  {user ? "Portal" : "Login"}
                </Link>
                <Button className="mt-3 rounded-[6px] bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold transition-colors duration-200" onClick={() => { setMobileOpen(false); navigate("/anfrage/erstgespraech"); }}>
                  {t("Erstgespr\u00e4ch vereinbaren", "Book initial consultation")}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const { t } = useLang();
  const site = useSiteConfig();
  const claim = useContent("footer.claim");
  return (
    <footer className="bg-[#111318] accent-hairline" data-testid="footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Logo dark />
            <p className="mt-5 text-sm text-white/55 leading-relaxed max-w-xs" data-testid="footer-claim">
              {claim.text || t(
                "Vier Bereiche, eine Verantwortung: Verm\u00f6gensverwaltung, internationale Strukturen, Immobilien und Vorsorge \u2013 analytisch, diskret und langfristig.",
                "Four divisions, one responsibility: wealth management, international structures, real estate and provision \u2013 analytical, discreet and long-term."
              )}
            </p>
            <address className="mt-5 not-italic text-sm text-white/55 leading-relaxed" data-testid="footer-address">
              {site.legal_name}<br />
              {site.street}<br />
              {site.zip_city}{", "}{site.country}
            </address>
          </div>
          <div className="md:col-span-2">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45 mb-4">{t("Leistungsbereiche", "Services")}</h4>
            <ul className="space-y-2.5 text-sm text-white/65">
              <li><Link to="/family-office" className="hover:text-white transition-colors">Family Office</Link></li>
              <li><Link to="/real-estate" className="hover:text-white transition-colors">Real Estate</Link></li>
              <li><Link to="/maklerservice" className="hover:text-white transition-colors">Maklerservice</Link></li>
              <li><Link to="/development" className="hover:text-white transition-colors">Development</Link></li>
              <li><Link to="/leistungen/festgeld" className="hover:text-white transition-colors">{t("Festgeld", "Fixed-Term")}</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45 mb-4">{t("Unternehmen", "Company")}</h4>
            <ul className="space-y-2.5 text-sm text-white/65">
              <li><Link to="/privatkunden" className="hover:text-white transition-colors">{t("Privatkunden", "Private Clients")}</Link></li>
              <li><Link to="/ueber-uns" className="hover:text-white transition-colors">{t("\u00dcber uns", "About us")}</Link></li>
              <li><Link to="/karriere" className="hover:text-white transition-colors">{t("Karriere & Partner", "Careers & Partners")}</Link></li>
              <li><Link to="/berater-finden" className="hover:text-white transition-colors">{t("Berater finden", "Find an advisor")}</Link></li>
              <li><Link to="/termin" className="hover:text-white transition-colors">{t("Termin buchen", "Book appointment")}</Link></li>
            </ul>
          </div>
          <div className="md:col-span-4">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45 mb-4">{t("Rechtliches", "Legal")}</h4>
            <ul className="space-y-2.5 text-sm text-white/65" data-testid="footer-legal-links">
              <li><Link to="/impressum" className="hover:text-white transition-colors">{t("Impressum", "Legal Notice")}</Link></li>
              <li><Link to="/datenschutz" className="hover:text-white transition-colors">{t("Datenschutz", "Privacy Policy")}</Link></li>
              <li><Link to="/login" className="hover:text-white transition-colors">{t("Mitarbeiter-Login", "Employee Login")}</Link></li>
            </ul>
            <div className="mt-6 flex items-start gap-2.5 text-xs text-white/40 leading-relaxed border border-white/[0.07] rounded-[8px] p-4 bg-white/[0.02]">
              <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-[#FF3C20]/70" />
              <span>
                {t(
                  "Alle Inhalte dienen der allgemeinen Information und stellen keine Anlage-, Steuer- oder Rechtsberatung dar. Kundengelder flie\u00dfen nicht an Eleven. Konditionen werden ausschlie\u00dflich nach individueller Pr\u00fcfung kommuniziert; eine Anfrage stellt kein Angebot dar.",
                  "All content is for general information only and does not constitute investment, tax or legal advice. Client funds are never transferred to Eleven. Conditions are communicated exclusively after individual assessment; an inquiry does not constitute an offer."
                )}
              </span>
            </div>
          </div>
        </div>
        <Separator className="my-10 bg-white/[0.07]" />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <span className="font-mono-num">{"© "}{new Date().getFullYear()}{" "}{site.legal_name}{" · "}{t("Alle Rechte vorbehalten.", "All rights reserved.")}</span>
          <LanguageSwitcher dark />
        </div>
      </div>
    </footer>
  );
}

function ConsentBanner() {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("eleven_consent")) {
      setVisible(true);
    }
  }, []);

  const save = (a, m) => {
    localStorage.setItem(
      "eleven_consent",
      JSON.stringify({ necessary: true, analytics: a, marketing: m, ts: new Date().toISOString() })
    );
    setVisible(false);
    setCustomOpen(false);
  };

  if (!visible) return null;

  return (
    <>
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
        className="fixed bottom-4 left-4 right-4 sm:right-auto sm:left-6 sm:max-w-md z-[60]"
        data-testid="consent-banner"
      >
        <div className="rounded-[8px] border border-border bg-white p-5">
          <h4 className="text-sm font-semibold text-[#111318]">{t("Datenschutz & Cookies", "Privacy & Cookies")}</h4>
          <p className="mt-2 text-xs text-[#3B4452] leading-relaxed">
            {t(
              "Wir verwenden technisch notwendige Cookies. Optionale Analyse- und Marketing-Cookies setzen wir nur mit Ihrer Einwilligung ein. Details in unserer",
              "We use technically necessary cookies. Optional analytics and marketing cookies are only used with your consent. Details in our"
            )}{" "}
            <Link to="/datenschutz" className="underline underline-offset-2 text-[#111318]">{t("Datenschutzerkl\u00e4rung", "privacy policy")}</Link>.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" className="rounded-[6px] bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold transition-colors duration-200" onClick={() => save(true, true)} data-testid="consent-accept-all">
              {t("Alle akzeptieren", "Accept all")}
            </Button>
            <Button size="sm" variant="outline" className="rounded-[8px]" onClick={() => save(false, false)} data-testid="consent-necessary-only">
              {t("Nur notwendige", "Necessary only")}
            </Button>
            <Button size="sm" variant="ghost" className="rounded-[8px] text-[#6B7280] hover:text-[#111318]" onClick={() => setCustomOpen(true)} data-testid="consent-customize">
              {t("Anpassen", "Customize")}
            </Button>
          </div>
        </div>
      </motion.div>
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("Consent-Einstellungen", "Consent Settings")}</DialogTitle>
            <DialogDescription>
              {t("W\u00e4hlen Sie, welche Kategorien Sie zulassen m\u00f6chten.", "Choose which categories you want to allow.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t("Notwendig", "Necessary")}</p>
                <p className="text-xs text-muted-foreground">{t("Immer aktiv", "Always active")}</p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t("Analyse", "Analytics")}</p>
                <p className="text-xs text-muted-foreground">{t("Anonyme Nutzungsstatistiken", "Anonymous usage statistics")}</p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="consent-analytics-switch" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Marketing</p>
                <p className="text-xs text-muted-foreground">{t("Personalisierte Inhalte", "Personalized content")}</p>
              </div>
              <Switch checked={marketing} onCheckedChange={setMarketing} data-testid="consent-marketing-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-[6px] bg-[#FF3C20] hover:bg-[#E8241B] transition-colors duration-200" onClick={() => save(analytics, marketing)} data-testid="consent-save-custom">
              {t("Auswahl speichern", "Save selection")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <ChatWidget />
      <ConsentBanner />
    </div>
  );
}
